import React from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import type { ButtonProps, SxProps, Theme } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useNavigate } from 'react-router-dom';

export interface VerAsientoContableButtonProps {
  nroAsiento?: number | null;
  /** Botón compacto solo ícono (útil en grillas de acciones). */
  iconOnly?: boolean;
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
  sx?: SxProps<Theme>;
  /** Por defecto ícono de libro mayor; en factura de venta suele usarse el outlined. */
  startIcon?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Navega al formulario de libro diario del asiento vinculado al documento.
 */
const VerAsientoContableButton: React.FC<VerAsientoContableButtonProps> = ({
  nroAsiento,
  iconOnly,
  variant = 'outlined',
  color = 'primary',
  sx,
  startIcon,
  disabled,
}) => {
  const navigate = useNavigate();

  const tieneAsiento = nroAsiento != null && nroAsiento > 0 && !disabled;

  const titulo = tieneAsiento
    ? 'Abrir el asiento en libro diario'
    : 'Este documento no tiene asiento en el libro diario';

  const onClick = () => {
    if (!tieneAsiento) return;
    navigate(`/contabilidad/asientos-diario/${nroAsiento}`);
  };

  const icon = startIcon ?? <AccountBalanceIcon />;

  if (iconOnly) {
    return (
      <Tooltip title={titulo}>
        <span>
          <IconButton
            size="small"
            disabled={!tieneAsiento}
            onClick={onClick}
            sx={{
              bgcolor: '#2196f3',
              color: '#fff',
              borderRadius: 1.5,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: '#1976d2' },
              '&:disabled': { bgcolor: '#ccc' },
              ...sx,
            }}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={titulo}>
      <span>
        <Button
          variant={variant}
          color={color}
          startIcon={icon}
          disabled={!tieneAsiento}
          onClick={onClick}
          sx={sx}
        >
          Ver asiento contable
        </Button>
      </span>
    </Tooltip>
  );
};

export default VerAsientoContableButton;
